export type TreasureFieldDriver = "value" | "quantity" | null;

export interface TreasureLink {
    linked: boolean;
    driver: TreasureFieldDriver;
}

export interface TreasureDraft {
    value: number;
    quantity: number;
    unit: number;
    /** Quantidade em carteira. Sem teto no aporte. */
    held?: number;
}

export interface TreasureLinkResult {
    link: TreasureLink;
    value?: number;
    quantity?: number;
}

export const freshTreasureLink = (): TreasureLink => ({ linked: true, driver: null });

const floorTo6 = (quantity: number) => Math.floor(quantity * 1e6 + 1e-6) / 1e6;

const roundMoney = (value: number) => Math.round(value * 100) / 100;

/** Sugestão valor ÷ PU, sem passar da quantidade em carteira. */
export const suggestedTreasureQuantity = (value: number, unit: number, held?: number) => {
    if (!(value > 0) || !(unit > 0)) {
        return 0;
    }
    const raw = value / unit;
    if (held != null && held > 0 && raw > held) {
        return floorTo6(held);
    }
    return Math.round(raw * 1e6) / 1e6;
};

/** O que o usuário digitou, cortado na quantidade da carteira. */
export const acceptedTreasureQuantity = (quantity: number, held?: number) => {
    if (!(quantity > 0)) {
        return quantity;
    }
    if (held != null && held > 0 && quantity > held + 1e-9) {
        return floorTo6(held);
    }
    return quantity;
};

/**
 * Uma sugestão liga valor e quantidade pelo PU.
 * No resgate a quantidade não passa da carteira, e o valor digitado permanece
 * mesmo quando não fecha quantidade × PU.
 * Depois que o usuário edita o outro campo, os dois deixam de se alterar.
 */
export const applyTreasureFieldEdit = (
    link: TreasureLink,
    field: "value" | "quantity" | "unit",
    draft: TreasureDraft
): TreasureLinkResult => {
    if (field === "quantity") {
        if (!(draft.quantity > 0)) {
            return link.driver === "value"
                ? { link: { linked: false, driver: "value" } }
                : { link };
        }
        const quantity = acceptedTreasureQuantity(draft.quantity, draft.held);
        if (!link.linked || link.driver === "value") {
            return { link: { linked: false, driver: link.driver ?? "value" }, quantity };
        }
        if (!(quantity > 0) || !(draft.unit > 0)) {
            return { link: { linked: true, driver: "quantity" }, quantity };
        }
        return {
            link: { linked: true, driver: "quantity" },
            quantity,
            value: roundMoney(quantity * draft.unit)
        };
    }

    if (field === "value") {
        if (!link.linked || link.driver === "quantity") {
            return { link: { linked: false, driver: link.driver ?? "quantity" } };
        }
        if (!(draft.value > 0) || !(draft.unit > 0)) {
            return { link: { linked: true, driver: "value" } };
        }
        return {
            link: { linked: true, driver: "value" },
            quantity: suggestedTreasureQuantity(draft.value, draft.unit, draft.held)
        };
    }

    if (!link.linked || !(draft.unit > 0)) {
        return { link };
    }
    if (link.driver === "quantity" && draft.quantity > 0) {
        const quantity = acceptedTreasureQuantity(draft.quantity, draft.held);
        return {
            link,
            quantity,
            value: roundMoney(quantity * draft.unit)
        };
    }
    if (draft.value > 0) {
        return {
            link: { linked: true, driver: link.driver ?? "value" },
            quantity: suggestedTreasureQuantity(draft.value, draft.unit, draft.held)
        };
    }
    return { link };
};
